import { useMemo, useState } from "react";

import {
  DefaultButton,
  Heading,
  MessageBar,
  MessageBarType,
  ScrollablePane,
  ScrollbarVisibility,
  Stack,
  StackItem,
  SubmissionFormButtons,
  Text,
  TextInputField,
  ToggleField,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { RolloutPackages } from "../rollout-package";
import { AddRolloutPackageDialog } from "../rollout-package/add";
import {
  RolloutPackageConfigureDto,
  RolloutPackageDesiredConfigDialog
} from "../rollout-package/configure";
import { RolloutCommencesLabel } from "./RolloutCommencesLabel";
import { RolloutDateShortcuts } from "./RolloutDateShortcuts";
import { isValidRolloutDateAndTime } from "./RolloutFormValidator";
import {
  RolloutFormValues,
  RolloutPackageDesiredConfigFormValues
} from "./rolloutFormValues";
import { RolloutTimePicker } from "./RolloutTimePicker";

interface RolloutFormProps {
  isReadOnly: boolean;
  error?: string;
}

export const RolloutForm: React.FC<RolloutFormProps> = ({
  isReadOnly,
  error
}) => {
  const theme = useTheme();

  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [showConfigure, setShowConfigure] = useState<boolean>(false);

  // The software package whose desired configuration we are editing.
  const [configureSoftwarePackage, setConfigureSoftwarePackage] = useState<
    RolloutPackageConfigureDto | undefined
  >();

  const { actions, state } = useFormContext<RolloutFormValues>();
  const validationError = useMemo(() => {
    const {
      initialDownloadDateStartUtc,
      initialDownloadDateEndUtc,
      initialDownloadTimeStartUtc,
      initialDownloadTimeEndUtc,
      initialInstallDateStartUtc,
      initialInstallDateEndUtc,
      initialInstallTimeStartUtc,
      initialInstallTimeEndUtc
    } = state.values;

    return isValidRolloutDateAndTime({
      downloadStartDate: initialDownloadDateStartUtc,
      downloadStartTime: initialDownloadTimeStartUtc,
      downloadEndDate: initialDownloadDateEndUtc,
      downloadEndTime: initialDownloadTimeEndUtc,
      installStartDate: initialInstallDateStartUtc,
      installStartTime: initialInstallTimeStartUtc,
      installEndDate: initialInstallDateEndUtc,
      installEndTime: initialInstallTimeEndUtc
    });
  }, [state.values]);

  // When we click the configure icon on a rollout package, set the current software package ID's and show the edit dialog.
  const configureRolloutPackage = (
    softwarePackageId: string,
    softwarePackageVersionId: string
  ) => {
    setConfigureSoftwarePackage({
      softwarePackageId,
      softwarePackageVersionId
    });

    setShowConfigure(true);
  };

  const onUpdateSoftwarePackageDesiredConfigs = (
    configs: RolloutPackageDesiredConfigFormValues[]
  ) => {
    const rolloutPackages = state.values.rolloutPackages;
    const updatedPackages =
      rolloutPackages?.map(rolloutPackage => {
        const { desiredConfigs, ...packageRest } = rolloutPackage;
        return {
          ...packageRest,
          desiredConfigs:
            rolloutPackage.softwarePackageId ===
            configureSoftwarePackage?.softwarePackageId
              ? configs
              : desiredConfigs
        };
      }) ?? [];

    actions.setValue("rolloutPackages", updatedPackages);
  };

  return (
    <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
      {validationError && (
        <MessageBar messageBarType={MessageBarType.error}>
          {validationError}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}

      <Stack horizontal>
        <StackItem>
          <Stack horizontal tokens={{ childrenGap: theme.spacing.l1 }}>
            <TextInputField
              name="displayName"
              label="Display Name"
              styles={{
                root: {
                  width: "585px"
                }
              }}
            />

            <ToggleField
              name="isActive"
              label="Enabled"
              data-test="switch-isactive"
              styles={{
                root: { margin: 0, flexGrow: 1 },
                container: { marginTop: 4 }
              }}
            />
          </Stack>
          <Stack>
            <StackItem>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: theme.spacing.m }}
              >
                <Heading labelPaddings>
                  Initial Download Window (UTC Time)
                </Heading>

                <RolloutCommencesLabel />
              </Stack>

              <Stack horizontal tokens={{ childrenGap: theme.spacing.l1 }}>
                <RolloutTimePicker
                  isReadOnly={isReadOnly}
                  datePickerName="initialDownloadDateStartUtc"
                  timePickerName="initialDownloadTimeStartUtc"
                  isStart={true}
                />

                <RolloutTimePicker
                  isReadOnly={isReadOnly}
                  datePickerName="initialDownloadDateEndUtc"
                  timePickerName="initialDownloadTimeEndUtc"
                  isStart={false}
                />
              </Stack>
            </StackItem>

            <StackItem>
              <Heading labelPaddings>
                Initial Installation Window (UTC Time)
              </Heading>
              <Stack horizontal tokens={{ childrenGap: theme.spacing.l1 }}>
                <RolloutTimePicker
                  isReadOnly={isReadOnly}
                  datePickerName="initialInstallDateStartUtc"
                  timePickerName="initialInstallTimeStartUtc"
                  isStart={true}
                />

                <RolloutTimePicker
                  isReadOnly={isReadOnly}
                  datePickerName="initialInstallDateEndUtc"
                  timePickerName="initialInstallTimeEndUtc"
                  isStart={false}
                />
              </Stack>
            </StackItem>
          </Stack>
        </StackItem>
        <StackItem>
          <RolloutDateShortcuts isReadOnly={isReadOnly} />
        </StackItem>
      </Stack>
      <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
        <Stack horizontal horizontalAlign="space-between">
          <Text variant="xLarge">Software Packages</Text>

          <Stack.Item align="end">
            <DefaultButton
              iconProps={{ iconName: "Add" }}
              onClick={() => setShowAdd(true)}
              disabled={isReadOnly || !hasDeployPermission}
              title={!hasDeployPermission ? NO_ACTION_PERMISSION : undefined}
            >
              Add Software Package
            </DefaultButton>
          </Stack.Item>
        </Stack>

        <ScrollablePane
          scrollbarVisibility={ScrollbarVisibility.auto}
          styles={{ root: { height: "100%", position: "relative" } }}
        >
          <RolloutPackages
            onConfigureRolloutPackage={configureRolloutPackage}
            isReadOnly={isReadOnly}
          />
          {showAdd && (
            <AddRolloutPackageDialog onDismiss={() => setShowAdd(false)} />
          )}
        </ScrollablePane>
      </Stack>

      <SubmissionFormButtons
        hideCancelButton
        submitButtonProps={{ disableSubmitOnFormInvalid: true }}
      />
      {configureSoftwarePackage && showConfigure && (
        <RolloutPackageDesiredConfigDialog
          onDismiss={() => setShowConfigure(false)}
          onUpdate={onUpdateSoftwarePackageDesiredConfigs}
          configureSoftwarePackage={configureSoftwarePackage}
          isReadOnly={isReadOnly}
        />
      )}
    </Stack>
  );
};
